import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const formatCurrency = (val) => Number(val || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

const Sparkline = ({ data = [] }) => {
  const width = 140;
  const height = 40;
  if (!data.length) {
    return <div className="status-text">No data</div>;
  }
  const counts = data.map((d) => Number(d.count) || 0);
  const max = Math.max(...counts, 1);
  const step = counts.length > 1 ? width / (counts.length - 1) : width;
  const points = counts.map((value, idx) => {
    const x = idx * step;
    const y = height - (value / max) * height;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ background: '#f5f7fa', borderRadius: '6px' }}>
      <polyline
        fill="none"
        stroke="#2f6fed"
        strokeWidth="2"
        points={points}
      />
    </svg>
  );
};

function AdminDashboard() {
  const [properties, setProperties] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [applications, setApplications] = useState([]);
  const [leases, setLeases] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [reports, setReports] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [supportThreads, setSupportThreads] = useState([]);
  const [supportThreadsLoading, setSupportThreadsLoading] = useState(false);
  const [supportThreadsError, setSupportThreadsError] = useState('');
  const [supportSelectedThread, setSupportSelectedThread] = useState(null);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportMessagesLoading, setSupportMessagesLoading] = useState(false);
  const [supportReplyText, setSupportReplyText] = useState('');
  const [supportReplySending, setSupportReplySending] = useState(false);
  const [supportReplyError, setSupportReplyError] = useState('');
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [activityFilters, setActivityFilters] = useState({
    userType: '',
    userId: '',
    eventType: '',
    search: ''
  });
  const [activityPagination, setActivityPagination] = useState({ limit: 100, offset: 0, total: 0 });
  const [feeForm, setFeeForm] = useState({
    platform_fee_type: 'percent',
    platform_fee_value: 1,
    pass_through_platform_fee: false,
    pass_through_card_fee: true,
    pass_through_pad_fee: false,
    processing_fee_mode: 'pass_through',
    pad_failure_fee_policy: 'pass_through'
  });
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeMessage, setFeeMessage] = useState('');
  const [refundForm, setRefundForm] = useState({ paymentId: '', amount: '', reason: 'requested_by_customer' });
  const [refundMessage, setRefundMessage] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [promoForm, setPromoForm] = useState({
    code: '',
    description: '',
    discount_type: 'full_access',
    discount_value: '',
    applies_to: 'both',
    plan_scope: 'all',
    duration_months: 1,
    max_redemptions: '',
    expires_at: ''
  });
  // Partner referral system state
  const [partners, setPartners] = useState([]);
  const [partnerForm, setPartnerForm] = useState({
    name: '', email: '', company: '', phone: '', referral_code: '',
    commission_type: 'flat', commission_value: '', commercial_commission_type: 'flat', commercial_commission_value: '', notes: '', payout_email: ''
  });
  const [editingPartnerId, setEditingPartnerId] = useState(null);
  const [partnerDetailId, setPartnerDetailId] = useState(null);
  const [partnerReferrals, setPartnerReferrals] = useState([]);
  const [partnerDashboard, setPartnerDashboard] = useState(null);

  const [fraudFlags, setFraudFlags] = useState([]);
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [analyticsSummary, setAnalyticsSummary] = useState({ flags: [], reports: [] });
  const [analyticsTrends, setAnalyticsTrends] = useState({ flags: [], reports: [] });
  const [riskLeaders, setRiskLeaders] = useState([]);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [trafficData, setTrafficData] = useState(null);
  const [trafficDays, setTrafficDays] = useState(7);
  const [trafficFeed, setTrafficFeed] = useState([]);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [trafficFilters, setTrafficFilters] = useState({ page: null, referrer: null, device: null, user_type: null });
  const [verificationQueue, setVerificationQueue] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedLandlordId, setSelectedLandlordId] = useState('');
  const [paymentEventFilter, setPaymentEventFilter] = useState('all');
  const [adminSearchLandlords, setAdminSearchLandlords] = useState('');
  const [adminSearchTenants, setAdminSearchTenants] = useState('');
  const [paymentSearch, setPaymentSearch] = useState('');
  const [reportStatusFilter, setReportStatusFilter] = useState('');
  const [flagStatusFilter, setFlagStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');

    const fetchAllProperties = async () => {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/properties`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setProperties(data.properties || []);
    };

    const fetchUsers = async () => {
      const [landlordRes, tenantRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/landlords`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/tenants`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      const landlordData = await landlordRes.json().catch(() => ({}));
      const tenantData = await tenantRes.json().catch(() => ({}));
      if (!landlordRes.ok) throw new Error(landlordData.error || 'Failed to load landlords');
      if (!tenantRes.ok) throw new Error(tenantData.error || 'Failed to load tenants');
      setLandlords(landlordData.landlords || []);
      setTenants(tenantData.tenants || []);
    };

    const fetchAdminData = async () => {
      const reportsQuery = new URLSearchParams();
      if (selectedLandlordId) reportsQuery.append('landlordId', selectedLandlordId);
      if (reportStatusFilter) reportsQuery.append('status', reportStatusFilter);
      const flagsQuery = new URLSearchParams();
      if (selectedLandlordId) flagsQuery.append('landlordId', selectedLandlordId);
      if (flagStatusFilter) flagsQuery.append('status', flagStatusFilter);

      const [appsRes, leasesRes, maintenanceRes, ledgersRes, paymentsRes, reportsRes, fraudRes, summaryRes, trendsRes, riskRes, activeUsersRes, verificationRes, promoRes, auditRes, partnersRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/leases`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/maintenance`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/ledgers`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/payments`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/property-reports${reportsQuery.toString() ? `?${reportsQuery.toString()}` : ''}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/fraud-flags${flagsQuery.toString() ? `?${flagsQuery.toString()}` : ''}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/analytics/fraud-summary?days=${analyticsDays}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/analytics/fraud-trends?days=${analyticsDays}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/analytics/landlord-risk`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/analytics/active-users`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/landlords/verification/pending`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/promo-codes`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/audit-logs?limit=50`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/partners`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      const appsData = await appsRes.json().catch(() => ({}));
      const leasesData = await leasesRes.json().catch(() => ({}));
      const maintenanceData = await maintenanceRes.json().catch(() => ({}));
      const ledgersData = await ledgersRes.json().catch(() => ({}));
      const paymentsData = await paymentsRes.json().catch(() => ({}));
      const reportsData = await reportsRes.json().catch(() => ({}));
      const fraudData = await fraudRes.json().catch(() => ({}));
      const summaryData = await summaryRes.json().catch(() => ({}));
      const trendsData = await trendsRes.json().catch(() => ({}));
      const riskData = await riskRes.json().catch(() => ({}));
      const activeUsersData = await activeUsersRes.json().catch(() => ({}));
      const verificationData = await verificationRes.json().catch(() => ({}));
      const promoData = await promoRes.json().catch(() => ({}));
      const auditData = await auditRes.json().catch(() => ({}));
      const partnersData = await partnersRes.json().catch(() => ({}));
      if (!appsRes.ok) throw new Error(appsData.error || 'Failed to load applications');
      if (!leasesRes.ok) throw new Error(leasesData.error || 'Failed to load leases');
      if (!maintenanceRes.ok) throw new Error(maintenanceData.error || 'Failed to load maintenance');
      if (!ledgersRes.ok) throw new Error(ledgersData.error || 'Failed to load ledgers');
      if (!paymentsRes.ok) throw new Error(paymentsData.error || 'Failed to load payments');
      if (!reportsRes.ok) throw new Error(reportsData.error || 'Failed to load reports');
      if (!fraudRes.ok) throw new Error(fraudData.error || 'Failed to load fraud flags');
      if (!summaryRes.ok) throw new Error(summaryData.error || 'Failed to load analytics summary');
      if (!trendsRes.ok) throw new Error(trendsData.error || 'Failed to load analytics trends');
      if (!riskRes.ok) throw new Error(riskData.error || 'Failed to load risk leaders');
      if (!verificationRes.ok) throw new Error(verificationData.error || 'Failed to load verification queue');
      if (!promoRes.ok) throw new Error(promoData.error || 'Failed to load promo codes');
      if (!auditRes.ok) throw new Error(auditData.error || 'Failed to load audit logs');
      setApplications(appsData.applications || []);
      setLeases(leasesData.leases || []);
      setMaintenance(maintenanceData.requests || []);
      setLedgers(ledgersData.ledgers || []);
      setPayments(paymentsData.payments || []);
      setReports(reportsData.reports || []);
      setFraudFlags(fraudData.flags || []);
      setAnalyticsSummary(summaryData || { flags: [], reports: [] });
      setAnalyticsTrends(trendsData || { flags: [], reports: [] });
      setRiskLeaders(riskData.landlords || []);
      setVerificationQueue(verificationData.items || []);
      setActiveUsersCount(activeUsersData.active_users || 0);
      setPromoCodes(promoData.promo_codes || []);
      setAuditLogs(auditData.logs || []);
      if (partnersRes.ok) setPartners(partnersData.partners || []);
    };

    const run = async () => {
      try {
        await Promise.all([fetchAllProperties(), fetchUsers(), fetchAdminData()]);
      } catch (err) {
        if (err.message.includes('403') || err.message.includes('401')) {
          navigate('/login');
        }
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [navigate, selectedLandlordId, reportStatusFilter, flagStatusFilter, analyticsDays]);

  // ── Traffic tab data fetching with auto-refresh ──
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (activeTab !== 'traffic' || !token) return;

    const buildTrafficURL = () => {
      const params = new URLSearchParams({ days: trafficDays });
      if (trafficFilters.page) params.set('page', trafficFilters.page);
      if (trafficFilters.referrer) params.set('referrer', trafficFilters.referrer);
      if (trafficFilters.device) params.set('device', trafficFilters.device);
      if (trafficFilters.user_type) params.set('user_type', trafficFilters.user_type);
      return `${process.env.REACT_APP_API_BASE_URL}/api/admin/analytics/traffic?${params}`;
    };

    const fetchTraffic = async () => {
      setTrafficLoading(true);
      try {
        const [statsRes, feedRes] = await Promise.all([
          fetch(buildTrafficURL(), { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/analytics/feed?limit=30`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (statsRes.ok) setTrafficData(await statsRes.json());
        if (feedRes.ok) { const f = await feedRes.json(); setTrafficFeed(f.events || []); }
      } catch (err) { console.error('Traffic fetch error:', err); }
      finally { setTrafficLoading(false); }
    };
    fetchTraffic();

    // Auto-refresh every 15 seconds
    const interval = setInterval(async () => {
      try {
        const [statsRes, feedRes] = await Promise.all([
          fetch(buildTrafficURL(), { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/analytics/feed?limit=30`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (statsRes.ok) setTrafficData(await statsRes.json());
        if (feedRes.ok) { const f = await feedRes.json(); setTrafficFeed(f.events || []); }
      } catch (err) { /* silent */ }
    }, 15000);

    return () => clearInterval(interval);
  }, [activeTab, trafficDays, trafficFilters]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (activeTab !== 'support' || !token) return;

    const loadThreads = async () => {
      setSupportThreadsLoading(true);
      setSupportThreadsError('');
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/support/threads`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to load support threads');
        setSupportThreads(data.threads || []);
        // Auto-select first thread only on initial load (no thread selected yet)
        setSupportSelectedThread(prev => {
          if (!prev && data.threads?.length) return data.threads[0];
          return prev;
        });
      } catch (err) {
        setSupportThreadsError(err.message || 'Failed to load support threads');
      } finally {
        setSupportThreadsLoading(false);
      }
    };

    loadThreads();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (activeTab !== 'support' || !supportSelectedThread || !token) return;

    const loadMessages = async () => {
      setSupportMessagesLoading(true);
      setSupportReplyError('');
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/support/threads/${supportSelectedThread.id}/messages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to load support messages');
        setSupportMessages(data.messages || []);
      } catch (err) {
        setSupportReplyError(err.message || 'Failed to load support messages');
      } finally {
        setSupportMessagesLoading(false);
      }
    };

    loadMessages();
  }, [activeTab, supportSelectedThread]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (activeTab !== 'activity' || !token) return;

    const loadActivity = async () => {
      setActivityLoading(true);
      setActivityError('');
      try {
        const query = new URLSearchParams();
        if (activityFilters.userType) query.append('userType', activityFilters.userType);
        if (activityFilters.userId) query.append('userId', activityFilters.userId);
        if (activityFilters.eventType) query.append('eventType', activityFilters.eventType);
        if (activityFilters.search) query.append('search', activityFilters.search);
        query.append('limit', activityPagination.limit);
        query.append('offset', activityPagination.offset);

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/activity?${query.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to load activity logs');
        setActivityLogs(data.items || []);
        setActivityPagination((prev) => ({ ...prev, total: data.total || 0 }));
      } catch (err) {
        setActivityError(err.message || 'Failed to load activity logs');
      } finally {
        setActivityLoading(false);
      }
    };

    loadActivity();
  }, [activeTab, activityFilters, activityPagination.limit, activityPagination.offset]);

  const handleSupportReply = async () => {
    const token = localStorage.getItem('token');
    if (!supportSelectedThread || !supportReplyText.trim() || supportReplySending || supportMessagesLoading || !token) return;
    // Capture thread ID at send time to prevent race conditions
    const targetThreadId = supportSelectedThread.id;
    const targetEmail = supportSelectedThread.user_email;
    setSupportReplySending(true);
    setSupportReplyError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/support/threads/${targetThreadId}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message_text: supportReplyText.trim(), support_agent_email: 'support@rentual.co' })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to send reply to ${targetEmail}`);
      setSupportReplyText('');
      // Only append to messages if we're still viewing the same thread
      if (supportSelectedThread?.id === targetThreadId) {
        setSupportMessages((prev) => [...prev, data.message]);
      }
      setSupportThreads((prev) => prev.map((thread) => (
        thread.id === targetThreadId
          ? { ...thread, latest_message: data.message.message_text, latest_message_at: data.message.created_at, latest_sender_type: data.message.sender_type }
          : thread
      )));
    } catch (err) {
      setSupportReplyError(err.message || 'Failed to send support reply');
    } finally {
      setSupportReplySending(false);
    }
  };

  const loadFeeSettings = async (landlordId) => {
    if (!landlordId) return;
    const token = localStorage.getItem('token');
    setFeeLoading(true);
    setFeeMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/landlords/${landlordId}/fees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to load fee settings');
      const fees = data.fees || {};
      setFeeForm({
        platform_fee_type: fees.platform_fee_type || 'percent',
        platform_fee_value: fees.platform_fee_value ?? fees.default_platform_fee_pct ?? 1,
        pass_through_platform_fee: !!fees.pass_through_platform_fee,
        pass_through_card_fee: fees.pass_through_card_fee ?? fees.pass_card_fee_to_tenant ?? true,
        pass_through_pad_fee: !!fees.pass_through_pad_fee,
        processing_fee_mode: fees.processing_fee_mode || 'pass_through',
        pad_failure_fee_policy: fees.pad_failure_fee_policy || 'pass_through'
      });
    } catch (err) {
      setError(err.message || 'Failed to load fee settings');
    } finally {
      setFeeLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'landlords') return;
    if (!selectedLandlordId) {
      setFeeForm({
        platform_fee_type: 'percent',
        platform_fee_value: 1,
        pass_through_platform_fee: false,
        pass_through_card_fee: true,
        pass_through_pad_fee: false,
        processing_fee_mode: 'pass_through',
        pad_failure_fee_policy: 'pass_through'
      });
      setFeeMessage('');
      return;
    }
    loadFeeSettings(selectedLandlordId);
  }, [activeTab, selectedLandlordId]);

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to permanently delete this property?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/properties/${propertyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) navigate('/login');
        throw new Error(data.error || 'Failed to delete property');
      }
      setProperties(prev => prev.filter(p => p.id !== propertyId));
    } catch (err) {
      setError(err.message || 'Failed to delete property');
    }
  };

  const toggleLandlord = async (id, is_disabled) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/landlords/${id}/disable`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ is_disabled })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setLandlords(prev => prev.map(l => l.id === id ? { ...l, is_disabled: data.landlord.is_disabled } : l));
    } else {
      setError(data.error || 'Failed to update landlord');
    }
  };

  const toggleLandlordBlock = async (id, is_blocked) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/landlords/${id}/block`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ is_blocked })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setLandlords(prev => prev.map(l => l.id === id ? { ...l, is_blocked: data.landlord.is_blocked } : l));
    } else {
      setError(data.error || 'Failed to update landlord');
    }
  };

  const saveFeeSettings = async () => {
    if (!selectedLandlordId) return;
    const token = localStorage.getItem('token');
    const parsedFeeValue = feeForm.platform_fee_value === '' || feeForm.platform_fee_value == null
      ? null
      : Number(feeForm.platform_fee_value);
    if (parsedFeeValue != null && Number.isNaN(parsedFeeValue)) {
      setError('Platform fee value must be a number');
      return;
    }

    setFeeSaving(true);
    setFeeMessage('');
    try {
      const payload = {
        platform_fee_type: feeForm.platform_fee_type,
        platform_fee_value: parsedFeeValue,
        pass_through_platform_fee: !!feeForm.pass_through_platform_fee,
        pass_through_card_fee: !!feeForm.pass_through_card_fee,
        pass_through_pad_fee: !!feeForm.pass_through_pad_fee,
        processing_fee_mode: feeForm.processing_fee_mode,
        pad_failure_fee_policy: feeForm.pad_failure_fee_policy
      };
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/landlords/${selectedLandlordId}/fees`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save fee settings');
      const fees = data.fees || {};
      setFeeForm({
        platform_fee_type: fees.platform_fee_type || 'percent',
        platform_fee_value: fees.platform_fee_value ?? fees.default_platform_fee_pct ?? 1,
        pass_through_platform_fee: !!fees.pass_through_platform_fee,
        pass_through_card_fee: fees.pass_through_card_fee ?? fees.pass_card_fee_to_tenant ?? true,
        pass_through_pad_fee: !!fees.pass_through_pad_fee,
        processing_fee_mode: fees.processing_fee_mode || 'pass_through',
        pad_failure_fee_policy: fees.pad_failure_fee_policy || 'pass_through'
      });
      setFeeMessage('Fee settings updated.');
    } catch (err) {
      setError(err.message || 'Failed to save fee settings');
    } finally {
      setFeeSaving(false);
    }
  };

  const toggleTenant = async (id, is_disabled) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/tenants/${id}/disable`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ is_disabled })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setTenants(prev => prev.map(t => t.id === id ? { ...t, is_disabled: data.tenant.is_disabled } : t));
    } else {
      setError(data.error || 'Failed to update tenant');
    }
  };

  const resetLandlordPassword = async (id) => {
    const password = window.prompt('Enter a temporary password (min 6 chars):');
    if (!password) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/landlords/${id}/reset-password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ password })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) setError(data.error || 'Failed to reset password');
  };

  const resetTenantPassword = async (id) => {
    const password = window.prompt('Enter a temporary password (min 6 chars):');
    if (!password) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/tenants/${id}/reset-password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ password })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) setError(data.error || 'Failed to reset password');
  };

  const handlePromoChange = (field, value) => {
    setPromoForm((prev) => ({ ...prev, [field]: value }));
  };

  const createPromoCode = async () => {
    const token = localStorage.getItem('token');
    const payload = {
      ...promoForm,
      discount_value: promoForm.discount_value ? Number(promoForm.discount_value) : null,
      duration_months: Number(promoForm.duration_months) || 1,
      max_redemptions: promoForm.max_redemptions ? Number(promoForm.max_redemptions) : null,
      expires_at: promoForm.expires_at ? new Date(promoForm.expires_at).toISOString() : null
    };
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/promo-codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setPromoCodes((prev) => [data.promo_code, ...prev]);
      setPromoForm({
        code: '',
        description: '',
        discount_type: 'full_access',
        discount_value: '',
        applies_to: 'both',
        plan_scope: 'all',
        duration_months: 1,
        max_redemptions: '',
        expires_at: ''
      });
    } else {
      setError(data.error || 'Failed to create promo code');
    }
  };

  const togglePromoActive = async (promoId, is_active) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/promo-codes/${promoId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ is_active })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setPromoCodes((prev) => prev.map((promo) => promo.id === promoId ? data.promo_code : promo));
    } else {
      setError(data.error || 'Failed to update promo code');
    }
  };

  // Partner management functions
  const handlePartnerChange = (field, value) => {
    setPartnerForm((prev) => ({ ...prev, [field]: value }));
  };

  const generateReferralCode = () => {
    const prefix = (partnerForm.company || partnerForm.name || 'REF').replace(/[^a-zA-Z]/g, '').substring(0, 3).toUpperCase();
    const suffix = Math.floor(1000 + Math.random() * 9000);
    setPartnerForm((prev) => ({ ...prev, referral_code: `${prefix}${suffix}` }));
  };

  const resetPartnerForm = () => {
    setPartnerForm({ name: '', email: '', company: '', phone: '', referral_code: '', commission_type: 'flat', commission_value: '', commercial_commission_type: 'flat', commercial_commission_value: '', notes: '', payout_email: '' });
    setEditingPartnerId(null);
  };

  const createOrUpdatePartner = async () => {
    const token = localStorage.getItem('token');
    const isEditing = !!editingPartnerId;
    const url = isEditing
      ? `${process.env.REACT_APP_API_BASE_URL}/api/admin/partners/${editingPartnerId}`
      : `${process.env.REACT_APP_API_BASE_URL}/api/admin/partners`;
    const res = await fetch(url, {
      method: isEditing ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        ...partnerForm,
        commission_value: partnerForm.commission_value ? Number(partnerForm.commission_value) : 0,
        commercial_commission_type: partnerForm.commercial_commission_type || 'flat',
        commercial_commission_value: partnerForm.commercial_commission_value ? Number(partnerForm.commercial_commission_value) : 0
      })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      if (isEditing) {
        setPartners((prev) => prev.map((p) => p.id === editingPartnerId ? data.partner : p));
      } else {
        setPartners((prev) => [data.partner, ...prev]);
      }
      resetPartnerForm();
    } else {
      setError(data.error || `Failed to ${isEditing ? 'update' : 'create'} partner.`);
    }
  };

  const editPartner = (partner) => {
    setEditingPartnerId(partner.id);
    setPartnerForm({
      name: partner.name || '',
      email: partner.email || '',
      company: partner.company || '',
      phone: partner.phone || '',
      referral_code: partner.referral_code || '',
      commission_type: partner.commission_type || 'flat',
      commission_value: partner.commission_value || '',
      commercial_commission_type: partner.commercial_commission_type || 'flat',
      commercial_commission_value: partner.commercial_commission_value || '',
      notes: partner.notes || '',
      payout_email: partner.payout_email || ''
    });
    setPartnerDetailId(null);
  };

  const togglePartnerStatus = async (partnerId, newStatus) => {
    const token = localStorage.getItem('token');
    const endpoint = newStatus === 'suspended' ? 'suspend' : 'activate';
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/partners/${partnerId}/${endpoint}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setPartners((prev) => prev.map((p) => p.id === partnerId ? data.partner : p));
    } else {
      setError(data.error || 'Failed to update partner status.');
    }
  };

  const viewPartnerDetail = async (partnerId) => {
    setPartnerDetailId(partnerId);
    setEditingPartnerId(null);
    const token = localStorage.getItem('token');
    try {
      const [refRes, dashRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/partners/${partnerId}/referrals`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/partners/${partnerId}/dashboard`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      const refData = await refRes.json().catch(() => ({}));
      const dashData = await dashRes.json().catch(() => ({}));
      setPartnerReferrals(refData.referrals || []);
      setPartnerDashboard(dashData);
    } catch (err) {
      console.error('Failed to load partner detail:', err);
    }
  };

  const copyReferralLink = (code) => {
    navigator.clipboard.writeText(`https://rentual.co/signup?ref=${code}`);
  };

  const updateApplicationStatus = async (id, status) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ status })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setApplications(prev => prev.map(a => a.id === id ? data.application : a));
    } else {
      setError(data.error || 'Failed to update application');
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setApplications(prev => prev.filter(a => a.id !== id));
    } else {
      setError(data.error || 'Failed to delete application');
    }
  };

  const viewConsentDocument = async (id) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/applications/${id}/consent-document`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || 'Failed to fetch consent document');
      return;
    }
    if (data.url) {
      window.open(data.url, '_blank', 'noopener,noreferrer');
      return;
    }
    alert(data.message || 'Consent document is stored locally on the server.');
  };

  const deleteLease = async (id) => {
    if (!window.confirm('Delete this lease document?')) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/leases/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setLeases(prev => prev.filter(l => l.id !== id));
    } else {
      setError(data.error || 'Failed to delete lease');
    }
  };

  const updateMaintenanceStatus = async (id, status) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/maintenance/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ status })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setMaintenance(prev => prev.map(m => m.id === id ? data.request : m));
    } else {
      setError(data.error || 'Failed to update maintenance');
    }
  };

  const deleteMaintenance = async (id) => {
    if (!window.confirm('Delete this maintenance request?')) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/maintenance/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setMaintenance(prev => prev.filter(m => m.id !== id));
    } else {
      setError(data.error || 'Failed to delete maintenance');
    }
  };

  const updateLedgerStatus = async (id, status) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/ledgers/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ status })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setLedgers(prev => prev.map(l => l.id === id ? data.ledger : l));
    } else {
      setError(data.error || 'Failed to update ledger');
    }
  };

  const deleteLedger = async (id) => {
    if (!window.confirm('Delete this ledger entry?')) return;
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/ledgers/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setLedgers(prev => prev.filter(l => l.id !== id));
    } else {
      setError(data.error || 'Failed to delete ledger');
    }
  };

  // Stripe payments are read-only in admin view.

  // Stripe payments are read-only in admin view.

  const updateReportStatus = async (id, status) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/property-reports/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ status })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setReports(prev => prev.map(r => r.id === id ? data.report : r));
    } else {
      setError(data.error || 'Failed to update report');
    }
  };

  const updateFraudStatus = async (id, status) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/fraud-flags/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ status })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setFraudFlags(prev => prev.map(f => f.id === id ? data.flag : f));
    } else {
      setError(data.error || 'Failed to update fraud flag');
    }
  };

  const updatePropertyStatus = async (id, status) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/properties/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ status })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || 'Failed to update property status');
    }
  };

  const verifyOwnership = async (landlordId, action) => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_API_BASE_URL}/api/admin/landlords/${landlordId}/verification/${action}`;
    const payload = action === 'reject'
      ? { notes: window.prompt('Rejection notes (optional):') }
      : undefined;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: payload ? JSON.stringify(payload) : undefined
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setVerificationQueue(prev => prev.filter(item => item.landlord_id !== landlordId));
    } else {
      setError(data.error || 'Failed to update verification');
    }
  };

  const unpauseLandlordListings = async (landlordId) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/landlords/${landlordId}/unpause`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || 'Failed to unpause listings');
    }
  };

  const clearLandlordFlags = async (landlordId) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/landlords/${landlordId}/clear-flags`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || 'Failed to clear flags');
    }
  };

  const jumpToAdminRecord = (type, id) => {
    if (!id) return;
    const targetTab = type === 'tenant' ? 'tenants' : 'landlords';
    setActiveTab(targetTab);
    setTimeout(() => {
      const el = document.getElementById(`admin-${type}-${id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  };

  const handleAdminRefund = async (e) => {
    e.preventDefault();
    if (!refundForm.paymentId) {
      setRefundMessage('Payment ID is required.');
      return;
    }
    const amountValue = refundForm.amount === '' ? null : Number(refundForm.amount);
    if (amountValue != null && !Number.isFinite(amountValue)) {
      setRefundMessage('Refund amount must be a number.');
      return;
    }
    setRefundLoading(true);
    setRefundMessage('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/${refundForm.paymentId}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ amount: amountValue, reason: refundForm.reason || null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Refund failed');
      setRefundMessage('Refund submitted to Stripe.');
      setRefundForm((prev) => ({ ...prev, paymentId: '', amount: '' }));
    } catch (err) {
      setRefundMessage(err.message || 'Refund failed');
    } finally {
      setRefundLoading(false);
    }
  };

  const copyToClipboard = (value) => {
    if (!value) return;
    navigator.clipboard?.writeText(value).catch(() => {});
  };

  if (loading) return <main className="App-main">Loading Admin Dashboard...</main>;
  if (error) return <main className="App-main"><p className="error-message">Error: {error}</p></main>;

  const openSecurityReports = reports.filter((report) => report.status === 'open').length;
  const openSecurityFlags = fraudFlags.filter((flag) => flag.status === 'open').length;
  const pendingVerifications = verificationQueue.filter((item) => item.ownership_status === 'pending').length;
  const activeUsers = activeUsersCount;
  const combinedUsers = [
    ...landlords.map((landlord) => ({
      ...landlord,
      role: landlord.is_admin ? 'admin' : 'landlord'
    })),
    ...tenants.map((tenant) => ({
      ...tenant,
      role: 'tenant'
    }))
  ].sort((a, b) => {
    const aTime = a.last_active ? new Date(a.last_active).getTime() : 0;
    const bTime = b.last_active ? new Date(b.last_active).getTime() : 0;
    return bTime - aTime;
  });
  const securityItemsTotal = openSecurityReports + openSecurityFlags + pendingVerifications;

  const normalizedPaymentSearch = paymentSearch.toLowerCase().trim();
  const filteredStripePayments = payments.filter((payment) => {
    const eventType = (payment.stripe_event_type || payment.stripe_object_type || '').toLowerCase();
    const eventMatch = paymentEventFilter === 'all'
      || (paymentEventFilter === 'disputes' && eventType.includes('dispute'))
      || (paymentEventFilter === 'refunds' && eventType.includes('refund'))
      || (paymentEventFilter === 'failed' && eventType.includes('failed'));
    const searchMatch = !normalizedPaymentSearch
      || eventType.includes(normalizedPaymentSearch)
      || (payment.customer_email || '').toLowerCase().includes(normalizedPaymentSearch)
      || (payment.landlord_email || '').toLowerCase().includes(normalizedPaymentSearch)
      || (payment.customer_id || '').toLowerCase().includes(normalizedPaymentSearch);
    return eventMatch && searchMatch;
  });

  const disputeRefundEvents = filteredStripePayments.filter((payment) => {
    const eventType = (payment.stripe_event_type || payment.stripe_object_type || '').toLowerCase();
    return eventType.includes('dispute') || eventType.includes('refund');
  });

  return (
    <main className="App-main admin-dashboard-page">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Admin</p>
          <h2>Admin Dashboard</h2>
          <p>Review platform security, verification, and system activity.</p>
        </div>
      </div>
      <div className="subnav-wrap" style={{ marginBottom: '1rem' }}>
        <div className="admin-tabs">
          <button className={activeTab === 'overview' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('overview')}>Overview</button>
          <button className={activeTab === 'properties' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('properties')}>Properties</button>
          <button className={activeTab === 'landlords' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('landlords')}>Landlords</button>
          <button className={activeTab === 'tenants' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('tenants')}>Tenants</button>
          <button className={activeTab === 'users' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('users')}>Users</button>
          <button className={activeTab === 'applications' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('applications')}>Applications</button>
          <button className={activeTab === 'leases' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('leases')}>Leases</button>
          <button className={activeTab === 'maintenance' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('maintenance')}>Maintenance</button>
          <button className={activeTab === 'ledgers' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('ledgers')}>Ledgers</button>
          <button className={activeTab === 'payments' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('payments')}>Payments</button>
          <button className={activeTab === 'reports' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('reports')}>Reports</button>
          <button className={activeTab === 'fraud' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('fraud')}>Fraud flags</button>
          <button className={activeTab === 'analytics' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('analytics')}>Analytics</button>
          <button className={activeTab === 'verifications' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('verifications')}>Verifications</button>
          <button className={activeTab === 'promos' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('promos')}>Promo codes</button>
          <button className={activeTab === 'partners' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('partners')}>Partners</button>
          <button className={activeTab === 'audits' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('audits')}>Audit logs</button>
          <button className={activeTab === 'support' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('support')}>Support chat</button>
          <button className={activeTab === 'traffic' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('traffic')}>📊 Traffic</button>
          <button className={activeTab === 'activity' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('activity')}>Activity log</button>
        </div>
      </div>
      {activeTab === 'overview' && (
        <>
          <section className="dashboard-section" style={{ marginBottom: '1rem' }}>
            <div className="section-header">
              <h3>Platform snapshot</h3>
              <span className="report-chip report-chip--muted">Basic KPIs</span>
            </div>
            <div className="kpi-grid" style={{ marginTop: '0.5rem' }}>
              <div className="kpi-card">
                <span>Properties</span>
                <strong>{properties.length}</strong>
              </div>
              <div className="kpi-card">
                <span>Landlords</span>
                <strong>{landlords.length}</strong>
              </div>
              <div className="kpi-card">
                <span>Tenants</span>
                <strong>{tenants.length}</strong>
              </div>
              <div className="kpi-card">
                <span>Active users</span>
                <strong>{activeUsers}</strong>
              </div>
              <div className="kpi-card">
                <span>Applications</span>
                <strong>{applications.length}</strong>
              </div>
              <div className="kpi-card">
                <span>Active leases</span>
                <strong>{leases.length}</strong>
              </div>
            </div>
          </section>
          {securityItemsTotal > 0 && (
            <section className="dashboard-section new-items-card" style={{ borderLeft: '4px solid #d93025', background: '#fff5f5', marginBottom: '1rem' }}>
              <div className="section-header">
                <h3>New security items</h3>
                <span className="report-chip report-chip--muted">Requires review</span>
              </div>
              <div className="kpi-grid" style={{ marginTop: '0.5rem' }}>
                <div className={`kpi-card ${openSecurityReports === 0 ? 'muted' : ''}`}>
                  <span>Open reports</span>
                  <strong>{openSecurityReports}</strong>
                  <div className="form-actions" style={{ marginTop: '0.35rem' }}>
                    <button className="secondary-button" onClick={() => setActiveTab('reports')}>Review</button>
                  </div>
                </div>
                <div className={`kpi-card ${openSecurityFlags === 0 ? 'muted' : ''}`}>
                  <span>Open fraud flags</span>
                  <strong>{openSecurityFlags}</strong>
                  <div className="form-actions" style={{ marginTop: '0.35rem' }}>
                    <button className="secondary-button" onClick={() => setActiveTab('fraud')}>Review</button>
                  </div>
                </div>
                <div className={`kpi-card ${pendingVerifications === 0 ? 'muted' : ''}`}>
                  <span>Ownership docs pending</span>
                  <strong>{pendingVerifications}</strong>
                  <div className="form-actions" style={{ marginTop: '0.35rem' }}>
                    <button className="secondary-button" onClick={() => setActiveTab('verifications')}>Review</button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {activeTab === 'properties' && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Address</th>
              <th>City</th>
              <th>Landlord ID</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((property) => (
              <tr key={property.id}>
                <td data-label="Address">{property.address}</td>
                <td data-label="City">{property.city}</td>
                <td data-label="Landlord ID">{property.landlord_id}</td>
                <td data-label="Status"><span className={`status-${property.status}`}>{property.status}</span></td>
                <td data-label="Actions" className="admin-table-actions">
                  <Link to={`/landlord/properties/edit/${property.id}`} className="secondary-button">Edit</Link>
                  <button onClick={() => handleDeleteProperty(property.id)} className="delete-button">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {activeTab === 'landlords' && (
        <>
          <section className="dashboard-section" style={{ marginBottom: '1rem' }}>
            <div className="section-header">
              <h3>Fee settings</h3>
              <span className="report-chip report-chip--muted">Admin-only</span>
            </div>
            <div className="form-group" style={{ maxWidth: '320px' }}>
              <label>Landlord</label>
              <select className="form-control" value={selectedLandlordId} onChange={(e) => setSelectedLandlordId(e.target.value)}>
                <option value="">Select a landlord</option>
                {landlords.map((landlord) => (
                  <option key={landlord.id} value={landlord.id}>{landlord.email}</option>
                ))}
              </select>
            </div>

            {feeLoading && <p className="status-text">Loading fee settings…</p>}
            {!feeLoading && !selectedLandlordId && (
              <p className="status-text">Select a landlord to edit their fee model.</p>
            )}

            {!feeLoading && selectedLandlordId && (
              <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                <div className="form-group">
                  <label>Platform fee type</label>
                  <select
                    className="form-control"
                    value={feeForm.platform_fee_type}
                    onChange={(e) => setFeeForm((prev) => ({ ...prev, platform_fee_type: e.target.value }))}
                  >
                    <option value="percent">Percent</option>
                    <option value="flat">Flat</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Platform fee value</label>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeForm.platform_fee_value}
                    onChange={(e) => setFeeForm((prev) => ({ ...prev, platform_fee_value: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Processing fee mode</label>
                  <select
                    className="form-control"
                    value={feeForm.processing_fee_mode}
                    onChange={(e) => setFeeForm((prev) => ({ ...prev, processing_fee_mode: e.target.value }))}
                  >
                    <option value="pass_through">Pass through</option>
                    <option value="absorb">Absorb (landlord pays)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>PAD failure fee policy</label>
                  <select
                    className="form-control"
                    value={feeForm.pad_failure_fee_policy}
                    onChange={(e) => setFeeForm((prev) => ({ ...prev, pad_failure_fee_policy: e.target.value }))}
                  >
                    <option value="pass_through">Pass through</option>
                    <option value="waive">Waive</option>
                    <option value="absorb">Absorb</option>
                  </select>
                </div>
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label className="status-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={!!feeForm.pass_through_platform_fee}
                      onChange={(e) => setFeeForm((prev) => ({ ...prev, pass_through_platform_fee: e.target.checked }))}
                    />
                    Pass through platform fee to tenant
                  </label>
                  <label className="status-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={!!feeForm.pass_through_card_fee}
                      onChange={(e) => setFeeForm((prev) => ({ ...prev, pass_through_card_fee: e.target.checked }))}
                    />
                    Pass through card processing fee
                  </label>
                  <label className="status-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={!!feeForm.pass_through_pad_fee}
                      onChange={(e) => setFeeForm((prev) => ({ ...prev, pass_through_pad_fee: e.target.checked }))}
                    />
                    Pass through PAD processing fee
                  </label>
                </div>
              </div>
            )}

            {feeMessage && <p className="status-text" style={{ marginTop: '0.5rem' }}>{feeMessage}</p>}
            <div className="form-actions" style={{ marginTop: '0.75rem' }}>
              <button
                className="secondary-button"
                onClick={() => selectedLandlordId && loadFeeSettings(selectedLandlordId)}
                disabled={!selectedLandlordId || feeLoading}
              >
                Refresh
              </button>
              <button
                className="primary-button"
                onClick={saveFeeSettings}
                disabled={!selectedLandlordId || feeSaving || feeLoading}
              >
                {feeSaving ? 'Saving…' : 'Save fee settings'}
              </button>
            </div>
          </section>

          <div style={{ marginBottom: '0.75rem' }}>
            <input type="text" placeholder="Search landlords by name or email..." value={adminSearchLandlords} onChange={(e) => setAdminSearchLandlords(e.target.value)} className="form-control" style={{ maxWidth: '400px' }} />
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Verified</th>
                <th>Disabled</th>
                <th>Blocked</th>
                <th>Open Flags</th>
                <th>Open Reports</th>
                <th>Risk</th>
                <th>Admin</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {landlords
                .filter((landlord) => !selectedLandlordId || String(landlord.id) === String(selectedLandlordId))
                .filter((landlord) => {
                  if (!adminSearchLandlords) return true;
                  const q = adminSearchLandlords.toLowerCase();
                  return (landlord.email || '').toLowerCase().includes(q)
                    || (landlord.first_name || '').toLowerCase().includes(q)
                    || (landlord.last_name || '').toLowerCase().includes(q)
                    || String(landlord.id).includes(q);
                })
                .map((landlord) => (
                <tr key={landlord.id} id={`admin-landlord-${landlord.id}`}>
                  <td data-label="Email">{landlord.email}</td>
                  <td data-label="Verified">{landlord.is_verified ? 'Yes' : 'No'}</td>
                  <td data-label="Disabled">{landlord.is_disabled ? 'Yes' : 'No'}</td>
                  <td data-label="Blocked">{landlord.is_blocked ? 'Yes' : 'No'}</td>
                  <td data-label="Open Flags">{landlord.open_flags ?? 0}</td>
                  <td data-label="Open Reports">{landlord.open_reports ?? 0}</td>
                  <td data-label="Risk">{(landlord.open_flags ?? 0) >= 5 ? 'High' : ((landlord.open_flags ?? 0) >= 3 || (landlord.open_reports ?? 0) >= 5) ? 'Medium' : 'Low'}</td>
                  <td data-label="Admin">{landlord.is_admin ? 'Yes' : 'No'}</td>
                  <td data-label="Actions" className="admin-table-actions">
                    <button className="secondary-button" onClick={() => toggleLandlord(landlord.id, !landlord.is_disabled)}>
                      {landlord.is_disabled ? 'Enable' : 'Disable'}
                    </button>
                    <button className="secondary-button" onClick={() => toggleLandlordBlock(landlord.id, !landlord.is_blocked)}>
                      {landlord.is_blocked ? 'Unblock' : 'Block'}
                    </button>
                    <button className="secondary-button" onClick={() => clearLandlordFlags(landlord.id)}>
                      Clear flags
                    </button>
                    <button className="secondary-button" onClick={() => unpauseLandlordListings(landlord.id)}>
                      Unpause listings
                    </button>
                    <button className="secondary-button" onClick={() => resetLandlordPassword(landlord.id)}>
                      Reset Password
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {activeTab === 'tenants' && (
        <>
        <div style={{ marginBottom: '0.75rem' }}>
          <input type="text" placeholder="Search tenants by name or email..." value={adminSearchTenants} onChange={(e) => setAdminSearchTenants(e.target.value)} className="form-control" style={{ maxWidth: '400px' }} />
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Verified</th>
              <th>Disabled</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.filter((tenant) => {
              if (!adminSearchTenants) return true;
              const q = adminSearchTenants.toLowerCase();
              return (tenant.email || '').toLowerCase().includes(q)
                || (tenant.first_name || '').toLowerCase().includes(q)
                || (tenant.last_name || '').toLowerCase().includes(q)
                || String(tenant.id).includes(q);
            }).map((tenant) => (
              <tr key={tenant.id} id={`admin-tenant-${tenant.id}`}>
                <td data-label="Email">{tenant.email}</td>
                <td data-label="Verified">{tenant.is_verified ? 'Yes' : 'No'}</td>
                <td data-label="Disabled">{tenant.is_disabled ? 'Yes' : 'No'}</td>
                <td data-label="Actions" className="admin-table-actions">
                  <button className="secondary-button" onClick={() => toggleTenant(tenant.id, !tenant.is_disabled)}>
                    {tenant.is_disabled ? 'Enable' : 'Disable'}
                  </button>
                  <button className="secondary-button" onClick={() => resetTenantPassword(tenant.id)}>
                    Reset Password
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}

      {activeTab === 'users' && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Verified</th>
              <th>Disabled</th>
              <th>Last Active</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {combinedUsers.map((user) => {
              const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || '—';
              return (
                <tr key={`${user.role}-${user.id}`}>
                  <td data-label="Role">{user.role}</td>
                  <td data-label="Name">{displayName}</td>
                  <td data-label="Email">{user.email}</td>
                  <td data-label="Phone">{user.phone || '—'}</td>
                  <td data-label="Verified">{user.is_verified ? 'Yes' : 'No'}</td>
                  <td data-label="Disabled">{user.is_disabled ? 'Yes' : 'No'}</td>
                  <td data-label="Last Active">{user.last_active ? new Date(user.last_active).toLocaleString() : '—'}</td>
                  <td data-label="Created">{user.created_at ? new Date(user.created_at).toLocaleString() : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {activeTab === 'applications' && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Property</th>
              <th>Landlord</th>
              <th>Status</th>
              <th>Consent</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {applications.map((app) => (
              <tr key={app.id}>
                <td data-label="Applicant">{app.full_name}</td>
                <td data-label="Property">{app.property_address}</td>
                <td data-label="Landlord">{app.landlord_email}</td>
                <td data-label="Status">{app.status}</td>
                <td data-label="Consent">
                  <div className="status-text" style={{ marginBottom: '0.35rem' }}>
                    {app.consent_data ? 'Captured' : 'Missing'}
                  </div>
                  <button
                    className="secondary-button"
                    onClick={() => viewConsentDocument(app.id)}
                    disabled={!app.consent_data}
                  >
                    View consent
                  </button>
                </td>
                <td data-label="Actions" className="admin-table-actions">
                  <select value={app.status} onChange={(e) => updateApplicationStatus(app.id, e.target.value)}>
                    <option value="submitted">submitted</option>
                    <option value="in_review">in_review</option>
                    <option value="docs_requested">docs_requested</option>
                    <option value="approved">approved</option>
                    <option value="lease_tenant_signed">lease_tenant_signed</option>
                    <option value="completed">completed</option>
                    <option value="rejected">rejected</option>
                  </select>
                  <button className="delete-button" onClick={() => deleteApplication(app.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {activeTab === 'leases' && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Tenant</th>
              <th>Landlord</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leases.length === 0 && (
              <tr>
                <td colSpan="5" className="status-text">No leases yet.</td>
              </tr>
            )}
            {leases.map((lease) => (
              <tr key={lease.id}>
                <td data-label="Property">{lease.property_address}</td>
                <td data-label="Tenant">{lease.tenant_email}</td>
                <td data-label="Landlord">{lease.landlord_email}</td>
                <td data-label="Status">{lease.status}</td>
                <td data-label="Actions" className="admin-table-actions">
                  <button className="delete-button" onClick={() => deleteLease(lease.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {activeTab === 'maintenance' && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Property</th>
              <th>Tenant</th>
              <th>Landlord</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {maintenance.length === 0 && (
              <tr>
                <td colSpan="6" className="status-text">No maintenance requests yet.</td>
              </tr>
            )}
            {maintenance.map((req) => (
              <tr key={req.id}>
                <td data-label="Title">{req.title}</td>
                <td data-label="Property">{req.property_address}</td>
                <td data-label="Tenant">{req.tenant_email}</td>
                <td data-label="Landlord">{req.landlord_email}</td>
                <td data-label="Status">{req.status}</td>
                <td data-label="Actions" className="admin-table-actions">
                  <select value={req.status} onChange={(e) => updateMaintenanceStatus(req.id, e.target.value)}>
                    <option value="open">open</option>
                    <option value="in_progress">in_progress</option>
                    <option value="resolved">resolved</option>
                  </select>
                  <button className="delete-button" onClick={() => deleteMaintenance(req.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {activeTab === 'ledgers' && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Tenant</th>
              <th>Landlord</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ledgers.map((ledger) => (
              <tr key={ledger.id}>
                <td data-label="Property">{ledger.property_address}</td>
                <td data-label="Tenant">{ledger.tenant_email}</td>
                <td data-label="Landlord">{ledger.landlord_email}</td>
                <td data-label="Amount">{formatCurrency(ledger.amount)}</td>
                <td data-label="Status">{ledger.status}</td>
                <td data-label="Due Date">{ledger.due_date ? new Date(ledger.due_date).toLocaleDateString() : '-'}</td>
                <td data-label="Actions" className="admin-table-actions">
                  <select value={ledger.status} onChange={(e) => updateLedgerStatus(ledger.id, e.target.value)}>
                    <option value="due">due</option>
                    <option value="paid">paid</option>
                    <option value="late">late</option>
                  </select>
                  <button className="delete-button" onClick={() => deleteLedger(ledger.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {activeTab === 'payments' && (
        <>
          <section className="dashboard-section" style={{ marginBottom: '1rem' }}>
            <div className="section-header">
              <h3>Disputes & refunds</h3>
              <span className="report-chip report-chip--muted">Stripe events</span>
            </div>
            {disputeRefundEvents.length === 0 ? (
              <p className="status-text">No dispute/refund events found.</p>
            ) : (
              <div className="ledger-grid" style={{ marginTop: '0.75rem' }}>
                {disputeRefundEvents.map((payment) => {
                  const eventType = payment.stripe_event_type || payment.stripe_object_type || '—';
                  const amount = payment.amount ? formatCurrency(payment.amount / 100) : '—';
                  const landlordMatch = landlords.find((landlord) => String(landlord.id) === String(payment.landlord_id))
                    || landlords.find((landlord) => landlord.email === payment.landlord_email);
                  const tenantMatch = tenants.find((tenant) => tenant.email === payment.customer_email);

                  return (
                    <div key={payment.id} className="ledger-item">
                      <div className="ledger-item-header">
                        <h3>{eventType}</h3>
                        <span className={`status-pill ${payment.status || 'pending'}`}>{payment.status || 'pending'}</span>
                      </div>
                      <p><strong>Amount:</strong> {amount}</p>
                      <p><strong>Customer:</strong> {payment.customer_email || payment.customer_id || '—'}</p>
                      <p><strong>Landlord:</strong> {payment.landlord_email || '—'}</p>
                      <p><strong>Created:</strong> {payment.created_at ? new Date(payment.created_at).toLocaleString() : (payment.received_at ? new Date(payment.received_at).toLocaleString() : '—')}</p>
                      <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                        <button className="secondary-button" onClick={() => copyToClipboard(payment.stripe_event_id)} disabled={!payment.stripe_event_id}>Copy event ID</button>
                        <button className="secondary-button" onClick={() => landlordMatch && jumpToAdminRecord('landlord', landlordMatch.id)} disabled={!landlordMatch}>Landlord profile</button>
                        <button className="secondary-button" onClick={() => tenantMatch && jumpToAdminRecord('tenant', tenantMatch.id)} disabled={!tenantMatch}>Tenant profile</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="dashboard-section" style={{ marginBottom: '1rem' }}>
            <div className="section-header">
              <h3>Refund actions</h3>
              <span className="report-chip report-chip--muted">Admin</span>
            </div>
            <form className="form-grid" onSubmit={handleAdminRefund} style={{ marginTop: '0.5rem' }}>
              <div className="form-group">
                <label>Payment ID</label>
                <input
                  className="form-control"
                  value={refundForm.paymentId}
                  onChange={(e) => setRefundForm((prev) => ({ ...prev, paymentId: e.target.value }))}
                  placeholder="Payments table id"
                  required
                />
              </div>
              <div className="form-group">
                <label>Refund amount (leave blank for full)</label>
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  step="0.01"
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm((prev) => ({ ...prev, amount: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Reason</label>
                <select
                  className="form-control"
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm((prev) => ({ ...prev, reason: e.target.value }))}
                >
                  <option value="requested_by_customer">requested_by_customer</option>
                  <option value="duplicate">duplicate</option>
                  <option value="fraudulent">fraudulent</option>
                </select>
              </div>
              <div className="form-actions" style={{ alignItems: 'flex-end' }}>
                <button className="primary-button" type="submit" disabled={refundLoading}>
                  {refundLoading ? 'Submitting…' : 'Issue refund'}
                </button>
              </div>
            </form>
            {refundMessage && <p className="status-text" style={{ marginTop: '0.5rem' }}>{refundMessage}</p>}
          </section>

          <section className="dashboard-section" style={{ marginBottom: '1rem' }}>
            <div className="section-header">
              <h3>Fee overrides</h3>
              <span className="report-chip report-chip--muted">Landlord-specific</span>
            </div>
            <div className="form-group" style={{ maxWidth: '320px' }}>
              <label>Landlord</label>
              <select className="form-control" value={selectedLandlordId} onChange={(e) => setSelectedLandlordId(e.target.value)}>
                <option value="">Select a landlord</option>
                {landlords.map((landlord) => (
                  <option key={landlord.id} value={landlord.id}>{landlord.email}</option>
                ))}
              </select>
            </div>

            {feeLoading && <p className="status-text">Loading fee settings…</p>}
            {!feeLoading && !selectedLandlordId && (
              <p className="status-text">Select a landlord to edit their fee model.</p>
            )}

            {!feeLoading && selectedLandlordId && (
              <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                <div className="form-group">
                  <label>Platform fee type</label>
                  <select
                    className="form-control"
                    value={feeForm.platform_fee_type}
                    onChange={(e) => setFeeForm((prev) => ({ ...prev, platform_fee_type: e.target.value }))}
                  >
                    <option value="percent">Percent</option>
                    <option value="flat">Flat</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Platform fee value</label>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    step="0.01"
                    value={feeForm.platform_fee_value}
                    onChange={(e) => setFeeForm((prev) => ({ ...prev, platform_fee_value: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Processing fee mode</label>
                  <select
                    className="form-control"
                    value={feeForm.processing_fee_mode}
                    onChange={(e) => setFeeForm((prev) => ({ ...prev, processing_fee_mode: e.target.value }))}
                  >
                    <option value="pass_through">Pass through</option>
                    <option value="absorb">Absorb (landlord pays)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>PAD failure fee policy</label>
                  <select
                    className="form-control"
                    value={feeForm.pad_failure_fee_policy}
                    onChange={(e) => setFeeForm((prev) => ({ ...prev, pad_failure_fee_policy: e.target.value }))}
                  >
                    <option value="pass_through">Pass through</option>
                    <option value="waive">Waive</option>
                    <option value="absorb">Absorb</option>
                  </select>
                </div>
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label className="status-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={!!feeForm.pass_through_platform_fee}
                      onChange={(e) => setFeeForm((prev) => ({ ...prev, pass_through_platform_fee: e.target.checked }))}
                    />
                    Pass through platform fee to tenant
                  </label>
                  <label className="status-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={!!feeForm.pass_through_card_fee}
                      onChange={(e) => setFeeForm((prev) => ({ ...prev, pass_through_card_fee: e.target.checked }))}
                    />
                    Pass through card processing fee
                  </label>
                  <label className="status-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={!!feeForm.pass_through_pad_fee}
                      onChange={(e) => setFeeForm((prev) => ({ ...prev, pass_through_pad_fee: e.target.checked }))}
                    />
                    Pass through PAD processing fee
                  </label>
                </div>
              </div>
            )}

            {feeMessage && <p className="status-text" style={{ marginTop: '0.5rem' }}>{feeMessage}</p>}
            <div className="form-actions" style={{ marginTop: '0.75rem' }}>
              <button
                className="secondary-button"
                onClick={() => selectedLandlordId && loadFeeSettings(selectedLandlordId)}
                disabled={!selectedLandlordId || feeLoading}
              >
                Refresh
              </button>
              <button
                className="primary-button"
                onClick={saveFeeSettings}
                disabled={!selectedLandlordId || feeSaving || feeLoading}
              >
                {feeSaving ? 'Saving…' : 'Save fee settings'}
              </button>
            </div>
          </section>

          <section className="dashboard-section">
            <div className="section-header">
              <h3>Stripe event log</h3>
              <span className="report-chip report-chip--muted">{filteredStripePayments.length} events</span>
            </div>
            <div className="ledger-filter-bar" style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <label className="status-text">
                Filter
                <select className="form-control" value={paymentEventFilter} onChange={(e) => setPaymentEventFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="disputes">Disputes</option>
                  <option value="refunds">Refunds</option>
                  <option value="failed">Failed</option>
                </select>
              </label>
              <label className="status-text" style={{ flex: 1, minWidth: '220px' }}>
                Search
                <input
                  className="form-control"
                  value={paymentSearch}
                  onChange={(e) => setPaymentSearch(e.target.value)}
                  placeholder="Event type, customer email, landlord"
                />
              </label>
            </div>
            <table className="admin-table" style={{ marginTop: '0.75rem' }}>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Landlord</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStripePayments.length === 0 && (
                  <tr>
                    <td colSpan="7" className="status-text">No payments yet.</td>
                  </tr>
                )}
                {filteredStripePayments.map((payment) => {
                  const landlordMatch = landlords.find((landlord) => String(landlord.id) === String(payment.landlord_id))
                    || landlords.find((landlord) => landlord.email === payment.landlord_email);
                  const tenantMatch = tenants.find((tenant) => tenant.email === payment.customer_email);

                  return (
                    <tr key={payment.id}>
                      <td data-label="Event">{payment.stripe_event_type || payment.stripe_object_type || '-'}</td>
                      <td data-label="Amount">{payment.amount ? formatCurrency(payment.amount / 100) : '-'}</td>
                      <td data-label="Status">{payment.status || '-'}</td>
                      <td data-label="Customer">{payment.customer_email || payment.customer_id || '-'}</td>
                      <td data-label="Landlord">{payment.landlord_email || '-'}</td>
                      <td data-label="Created">{payment.created_at ? new Date(payment.created_at).toLocaleString() : (payment.received_at ? new Date(payment.received_at).toLocaleString() : '-')}</td>
                      <td data-label="Actions" className="admin-table-actions">
                        <button className="secondary-button" onClick={() => copyToClipboard(payment.stripe_event_id)} disabled={!payment.stripe_event_id}>Copy ID</button>
                        <button className="secondary-button" onClick={() => landlordMatch && jumpToAdminRecord('landlord', landlordMatch.id)} disabled={!landlordMatch}>Landlord</button>
                        <button className="secondary-button" onClick={() => tenantMatch && jumpToAdminRecord('tenant', tenantMatch.id)} disabled={!tenantMatch}>Tenant</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </>
      )}

      {activeTab === 'reports' && (
        <>
          <div className="dashboard-section">
            <div className="form-group">
              <label>Filter by landlord</label>
              <select className="form-control" value={selectedLandlordId} onChange={(e) => setSelectedLandlordId(e.target.value)}>
                <option value="">All landlords</option>
                {landlords.map((landlord) => (
                  <option key={landlord.id} value={landlord.id}>{landlord.email}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select className="form-control" value={reportStatusFilter} onChange={(e) => setReportStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="open">open</option>
                <option value="reviewing">reviewing</option>
                <option value="resolved">resolved</option>
                <option value="dismissed">dismissed</option>
              </select>
              <div style={{ marginTop: '0.5rem' }}>
                <button className="secondary-button" onClick={() => setReportStatusFilter('open')}>Open only</button>
                <button className="secondary-button" onClick={() => setReportStatusFilter('')}>Clear</button>
              </div>
            </div>
          </div>
          <div className="dashboard-section" style={{ marginBottom: '1rem' }}>
            <button className="secondary-button" onClick={() => {
              const token = localStorage.getItem('token');
              fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/property-reports/bulk-resolve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ status: reportStatusFilter || 'open' })
              }).then(() => setReportStatusFilter(''));
            }}>Resolve filtered</button>
            <button className="secondary-button" onClick={() => window.open(`${process.env.REACT_APP_API_BASE_URL}/api/admin/exports/reports.csv`, '_blank')}>Export CSV</button>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>City</th>
                <th>Risk</th>
                <th>Reporter</th>
                <th>Reason</th>
                <th>IP</th>
                <th>Status</th>
                <th>Message</th>
                <th>Actions</th>
                <th>Created</th>
              </tr>
            </thead>
          <tbody>
            {reports.map((report) => (
              <tr key={report.id}>
                <td data-label="Property">{report.address || '—'}</td>
                <td data-label="City">{report.city || '—'}</td>
                <td data-label="Risk">
                  {(landlords.find(l => l.id === report.landlord_id)?.open_flags ?? 0) >= 5 ? 'High' : ((landlords.find(l => l.id === report.landlord_id)?.open_flags ?? 0) >= 3 || (landlords.find(l => l.id === report.landlord_id)?.open_reports ?? 0) >= 5) ? 'Medium' : 'Low'}
                </td>
                <td data-label="Reporter">
                  {report.reporter_type}{report.reporter_id ? ` #${report.reporter_id}` : ''}
                </td>
                <td data-label="Reason">{report.reason || '—'}</td>
                <td data-label="IP">{report.reporter_ip || '—'}</td>
                <td data-label="Status">
                  <select value={report.status} onChange={(e) => updateReportStatus(report.id, e.target.value)}>
                    <option value="open">open</option>
                    <option value="reviewing">reviewing</option>
                    <option value="resolved">resolved</option>
                    <option value="dismissed">dismissed</option>
                  </select>
                </td>
                <td data-label="Message">{report.message || '—'}</td>
                <td data-label="Actions" className="admin-table-actions">
                  {report.property_id && (
                    <>
                      <button className="secondary-button" onClick={() => updatePropertyStatus(report.property_id, 'off_market')}>Pause listing</button>
                      <button className="secondary-button" onClick={() => updatePropertyStatus(report.property_id, 'available')}>Unpause</button>
                    </>
                  )}
                  {report.landlord_id && (
                    <button className="secondary-button" onClick={() => setSelectedLandlordId(String(report.landlord_id))}>Filter landlord</button>
                  )}
                </td>
                <td data-label="Created">{report.created_at ? new Date(report.created_at).toLocaleDateString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
      )}

      {activeTab === 'fraud' && (
        <>
          <div className="dashboard-section">
            <div className="form-group">
              <label>Filter by landlord</label>
              <select className="form-control" value={selectedLandlordId} onChange={(e) => setSelectedLandlordId(e.target.value)}>
                <option value="">All landlords</option>
                {landlords.map((landlord) => (
                  <option key={landlord.id} value={landlord.id}>{landlord.email}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select className="form-control" value={flagStatusFilter} onChange={(e) => setFlagStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="open">open</option>
                <option value="reviewing">reviewing</option>
                <option value="resolved">resolved</option>
                <option value="dismissed">dismissed</option>
              </select>
              <div style={{ marginTop: '0.5rem' }}>
                <button className="secondary-button" onClick={() => setFlagStatusFilter('open')}>Open only</button>
                <button className="secondary-button" onClick={() => setFlagStatusFilter('')}>Clear</button>
              </div>
            </div>
          </div>
          <div className="dashboard-section" style={{ marginBottom: '1rem' }}>
            <button className="secondary-button" onClick={() => {
              const token = localStorage.getItem('token');
              fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/fraud-flags/bulk-resolve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ status: flagStatusFilter || 'open' })
              }).then(() => setFlagStatusFilter(''));
            }}>Resolve filtered</button>
            <button className="secondary-button" onClick={() => window.open(`${process.env.REACT_APP_API_BASE_URL}/api/admin/exports/fraud-flags.csv`, '_blank')}>Export CSV</button>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Signal</th>
                <th>Severity</th>
                <th>Risk</th>
                <th>Landlord</th>
                <th>Property</th>
                <th>Status</th>
                <th>Actions</th>
                <th>Created</th>
              </tr>
            </thead>
          <tbody>
            {fraudFlags.map((flag) => (
              <tr key={flag.id}>
                <td data-label="Signal">{flag.signal}</td>
                <td data-label="Severity">{flag.severity}</td>
                <td data-label="Risk">
                  {(landlords.find(l => l.id === flag.landlord_id)?.open_flags ?? 0) >= 5 ? 'High' : ((landlords.find(l => l.id === flag.landlord_id)?.open_flags ?? 0) >= 3 || (landlords.find(l => l.id === flag.landlord_id)?.open_reports ?? 0) >= 5) ? 'Medium' : 'Low'}
                </td>
                <td data-label="Landlord">{flag.landlord_email || flag.landlord_id || '—'}</td>
                <td data-label="Property">{flag.property_address || flag.property_id || '—'}</td>
                <td data-label="Status">
                  <select value={flag.status} onChange={(e) => updateFraudStatus(flag.id, e.target.value)}>
                    <option value="open">open</option>
                    <option value="reviewing">reviewing</option>
                    <option value="resolved">resolved</option>
                    <option value="dismissed">dismissed</option>
                  </select>
                </td>
                <td data-label="Actions" className="admin-table-actions">
                  {flag.property_id && (
                    <>
                      <button className="secondary-button" onClick={() => updatePropertyStatus(flag.property_id, 'off_market')}>Pause listing</button>
                      <button className="secondary-button" onClick={() => updatePropertyStatus(flag.property_id, 'available')}>Unpause</button>
                    </>
                  )}
                  {flag.landlord_id && (
                    <>
                      <button className="secondary-button" onClick={() => unpauseLandlordListings(flag.landlord_id)}>Unpause landlord</button>
                      <button className="secondary-button" onClick={() => clearLandlordFlags(flag.landlord_id)}>Clear flags</button>
                      <button className="secondary-button" onClick={() => setSelectedLandlordId(String(flag.landlord_id))}>Filter landlord</button>
                    </>
                  )}
                </td>
                <td data-label="Created">{flag.created_at ? new Date(flag.created_at).toLocaleDateString() : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
      )}

      {activeTab === 'analytics' && (
        <>
          <div className="dashboard-section">
            <div className="form-group">
              <label>Range (days)</label>
              <input
                type="number"
                min="1"
                max="365"
                className="form-control"
                value={analyticsDays}
                onChange={(e) => setAnalyticsDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
              />
            </div>
          </div>
          <div className="dashboard-section">
            <h3>Fraud summary (last {analyticsDays} days)</h3>
            <ul>
              {analyticsSummary.flags.map((item) => (
                <li key={`${item.signal}-${item.severity}`}>{item.signal} ({item.severity}): {item.count}</li>
              ))}
              {analyticsSummary.flags.length === 0 && <li>No flags in last {analyticsDays} days.</li>}
            </ul>
            <h4>Reports by status</h4>
            <ul>
              {analyticsSummary.reports.map((item) => (
                <li key={item.status}>{item.status}: {item.count}</li>
              ))}
              {analyticsSummary.reports.length === 0 && <li>No reports in last {analyticsDays} days.</li>}
            </ul>
          </div>
          <div className="dashboard-section">
            <h3>Trends (last {analyticsDays} days)</h3>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div>
                <p className="status-text">Flags</p>
                <Sparkline data={analyticsTrends.flags} />
              </div>
              <div>
                <p className="status-text">Reports</p>
                <Sparkline data={analyticsTrends.reports} />
              </div>
            </div>
          </div>
          <div className="dashboard-section">
            <h3>Landlord risk leaders</h3>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Landlord</th>
                  <th>Open Flags</th>
                  <th>High Flags</th>
                  <th>Open Reports</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {riskLeaders.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Landlord">{row.email}</td>
                    <td data-label="Open Flags">{row.open_flags}</td>
                    <td data-label="High Flags">{row.high_flags}</td>
                    <td data-label="Open Reports">{row.open_reports}</td>
                    <td data-label="Actions" className="admin-table-actions">
                      <button className="secondary-button" onClick={() => setSelectedLandlordId(String(row.id))}>Filter landlord</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'verifications' && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Landlord</th>
              <th>Ownership Status</th>
              <th>Ownership Doc</th>
              <th>ID Status</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {verificationQueue.length === 0 && (
              <tr>
                <td colSpan="6" className="status-text">No ownership documents awaiting review.</td>
              </tr>
            )}
            {verificationQueue.map((item) => (
              <tr key={item.landlord_id}>
                <td data-label="Landlord">{item.email}</td>
                <td data-label="Ownership Status">{item.ownership_status}</td>
                <td data-label="Ownership Doc">
                  {item.ownership_doc_url ? (
                    <a href={item.ownership_doc_url} target="_blank" rel="noreferrer">View doc</a>
                  ) : '—'}
                </td>
                <td data-label="ID Status">{item.id_status || 'unverified'}</td>
                <td data-label="Updated">{item.updated_at ? new Date(item.updated_at).toLocaleDateString() : '-'}</td>
                <td data-label="Actions" className="admin-table-actions">
                  <button className="secondary-button" onClick={() => verifyOwnership(item.landlord_id, 'approve')}>Approve</button>
                  <button className="secondary-button" onClick={() => verifyOwnership(item.landlord_id, 'reject')}>Reject</button>
                  {item.id_status !== 'verified' && (
                    <button className="secondary-button" onClick={() => verifyOwnership(item.landlord_id, 'id-verified')}>Mark ID verified</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {activeTab === 'promos' && (
        <>
          <div className="dashboard-section">
            <h3>Create promo code</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Code</label>
                <input className="form-control" value={promoForm.code} onChange={(e) => handlePromoChange('code', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input className="form-control" value={promoForm.description} onChange={(e) => handlePromoChange('description', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Type</label>
                <select className="form-control" value={promoForm.discount_type} onChange={(e) => handlePromoChange('discount_type', e.target.value)}>
                  <option value="full_access">Full access</option>
                  <option value="free_months">Free months</option>
                  <option value="percent">Percent off</option>
                  <option value="amount">Amount off</option>
                </select>
              </div>
              <div className="form-group">
                <label>Value</label>
                <input className="form-control" value={promoForm.discount_value} onChange={(e) => handlePromoChange('discount_value', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Applies to</label>
                <select className="form-control" value={promoForm.applies_to} onChange={(e) => handlePromoChange('applies_to', e.target.value)}>
                  <option value="both">Monthly + annual</option>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div className="form-group">
                <label>Plan scope</label>
                <select className="form-control" value={promoForm.plan_scope} onChange={(e) => handlePromoChange('plan_scope', e.target.value)}>
                  <option value="all">All plans</option>
                  <option value="pro">Pro</option>
                  <option value="pro_plus">Pro Plus</option>
                </select>
              </div>
              <div className="form-group">
                <label>Duration (months)</label>
                <input className="form-control" type="number" min="1" value={promoForm.duration_months} onChange={(e) => handlePromoChange('duration_months', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Max redemptions</label>
                <input className="form-control" type="number" min="1" value={promoForm.max_redemptions} onChange={(e) => handlePromoChange('max_redemptions', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Expires at</label>
                <input className="form-control" type="date" value={promoForm.expires_at} onChange={(e) => handlePromoChange('expires_at', e.target.value)} />
              </div>
            </div>
            <div className="form-actions">
              <button className="primary-button" onClick={createPromoCode} disabled={!promoForm.code}>Create promo</button>
            </div>
          </div>
          <div className="dashboard-section">
            <h3>Promo codes</h3>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Duration</th>
                  <th>Used</th>
                  <th>Max</th>
                  <th>Partner</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {promoCodes.length === 0 && (
                  <tr>
                    <td colSpan="9" className="status-text">No promo codes yet.</td>
                  </tr>
                )}
                {promoCodes.map((promo) => (
                  <tr key={promo.id}>
                    <td data-label="Code"><strong>{promo.code}</strong></td>
                    <td data-label="Type">{promo.discount_type === 'percent' ? `${promo.discount_value}% off` : promo.discount_type === 'amount' ? `$${promo.discount_value} off` : promo.discount_type === 'free_months' ? `${promo.duration_months}mo free` : 'Full access'}</td>
                    <td data-label="Value">{promo.discount_value || '-'}</td>
                    <td data-label="Duration">{promo.duration_months ? `${promo.duration_months}mo` : '-'}</td>
                    <td data-label="Used">{promo.redemption_count || 0}</td>
                    <td data-label="Max">{promo.max_redemptions || 'Unlimited'}</td>
                    <td data-label="Partner">{promo.partner_name || '-'}</td>
                    <td data-label="Active">{promo.is_active ? 'Yes' : 'No'}</td>
                    <td data-label="Actions" className="admin-table-actions">
                      {promo.is_active ? (
                        <button className="secondary-button" onClick={() => togglePromoActive(promo.id, false)}>Deactivate</button>
                      ) : (
                        <button className="secondary-button" onClick={() => togglePromoActive(promo.id, true)}>Activate</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'partners' && (
        <>
          {/* Partner detail view */}
          {partnerDetailId && (() => {
            const p = partners.find((x) => x.id === partnerDetailId);
            return (
              <div className="dashboard-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h3>{p?.name || 'Partner'} - {p?.company || ''}</h3>
                  <button className="secondary-button" onClick={() => { setPartnerDetailId(null); setPartnerDashboard(null); setPartnerReferrals([]); }}>Back to list</button>
                </div>
                {partnerDashboard && (
                  <div className="form-grid" style={{ marginBottom: '1rem' }}>
                    <div><strong>Total referrals:</strong> {partnerDashboard.total_referrals}</div>
                    <div><strong>Conversions:</strong> {partnerDashboard.total_conversions}</div>
                    <div><strong>Revenue:</strong> {formatCurrency(partnerDashboard.total_revenue)}</div>
                    <div><strong>Conversion rate:</strong> {partnerDashboard.conversion_rate}%</div>
                  </div>
                )}
                <h4>Referrals</h4>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Landlord</th>
                      <th>Email</th>
                      <th>Signup date</th>
                      <th>Conversion date</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerReferrals.length === 0 && (
                      <tr><td colSpan="5" className="status-text">No referrals yet.</td></tr>
                    )}
                    {partnerReferrals.map((ref) => (
                      <tr key={ref.id}>
                        <td data-label="Landlord">{ref.landlord_first_name} {ref.landlord_last_name}</td>
                        <td data-label="Email">{ref.landlord_email}</td>
                        <td data-label="Signup">{ref.referred_at ? new Date(ref.referred_at).toLocaleDateString() : '-'}</td>
                        <td data-label="Converted">{ref.converted_at ? new Date(ref.converted_at).toLocaleDateString() : '-'}</td>
                        <td data-label="Revenue">{formatCurrency(ref.revenue_generated)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Create / edit partner form */}
          {!partnerDetailId && (
            <div className="dashboard-section">
              <h3>{editingPartnerId ? 'Edit partner' : 'Add partner'}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input className="form-control" value={partnerForm.name} onChange={(e) => handlePartnerChange('name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input className="form-control" type="email" value={partnerForm.email} onChange={(e) => handlePartnerChange('email', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Company</label>
                  <input className="form-control" value={partnerForm.company} onChange={(e) => handlePartnerChange('company', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input className="form-control" value={partnerForm.phone} onChange={(e) => handlePartnerChange('phone', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Referral code</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input className="form-control" value={partnerForm.referral_code} onChange={(e) => handlePartnerChange('referral_code', e.target.value)} placeholder="Auto-generated if blank" />
                    <button type="button" className="secondary-button" onClick={generateReferralCode} style={{ whiteSpace: 'nowrap' }}>Generate</button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Consumer commission type</label>
                  <select className="form-control" value={partnerForm.commission_type} onChange={(e) => handlePartnerChange('commission_type', e.target.value)}>
                    <option value="flat">Flat (CAD)</option>
                    <option value="percent_all">% of all revenue</option>
                    <option value="percent_subscription">% of subscription</option>
                    <option value="percent_transactions">% of transactions</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Consumer commission value</label>
                  <input className="form-control" type="number" min="0" step="0.01" value={partnerForm.commission_value} onChange={(e) => handlePartnerChange('commission_value', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Commercial commission type</label>
                  <select className="form-control" value={partnerForm.commercial_commission_type} onChange={(e) => handlePartnerChange('commercial_commission_type', e.target.value)}>
                    <option value="flat">Flat ($)</option>
                    <option value="percent_all">% of all revenue</option>
                    <option value="percent_subscription">% of subscription</option>
                    <option value="percent_transactions">% of transactions</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Commercial commission value</label>
                  <input className="form-control" type="number" min="0" step="0.01" value={partnerForm.commercial_commission_value} onChange={(e) => handlePartnerChange('commercial_commission_value', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Payout email</label>
                  <input className="form-control" type="email" value={partnerForm.payout_email} onChange={(e) => handlePartnerChange('payout_email', e.target.value)} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Notes</label>
                  <textarea className="form-control" rows="2" value={partnerForm.notes} onChange={(e) => handlePartnerChange('notes', e.target.value)} />
                </div>
              </div>
              <div className="form-actions">
                <button className="primary-button" onClick={createOrUpdatePartner} disabled={!partnerForm.name || !partnerForm.email}>
                  {editingPartnerId ? 'Update partner' : 'Add partner'}
                </button>
                {editingPartnerId && (
                  <button className="secondary-button" onClick={resetPartnerForm}>Cancel</button>
                )}
              </div>
            </div>
          )}

          {/* Partners list */}
          {!partnerDetailId && (
            <div className="dashboard-section">
              <h3>Partners</h3>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Email</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th>Referrals</th>
                    <th>Conversions</th>
                    <th>Revenue</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.length === 0 && (
                    <tr><td colSpan="9" className="status-text">No partners yet.</td></tr>
                  )}
                  {partners.map((p) => (
                    <tr key={p.id}>
                      <td data-label="Name">{p.name}</td>
                      <td data-label="Company">{p.company || '-'}</td>
                      <td data-label="Email">{p.email}</td>
                      <td data-label="Code">
                        <span style={{ fontFamily: 'monospace' }}>{p.referral_code}</span>
                        <button className="secondary-button" onClick={() => copyReferralLink(p.referral_code)} style={{ marginLeft: '0.5rem', padding: '2px 8px', fontSize: '0.8em' }} title="Copy referral link">Copy link</button>
                      </td>
                      <td data-label="Status"><span className={p.status === 'active' ? 'status-badge status-active' : 'status-badge status-suspended'}>{p.status}</span></td>
                      <td data-label="Referrals">{p.total_referrals || 0}</td>
                      <td data-label="Conversions">{p.total_conversions || 0}</td>
                      <td data-label="Revenue">{formatCurrency(p.total_revenue)}</td>
                      <td data-label="Actions" className="admin-table-actions">
                        <button className="secondary-button" onClick={() => viewPartnerDetail(p.id)}>View</button>
                        <button className="secondary-button" onClick={() => editPartner(p)}>Edit</button>
                        {p.status === 'active' ? (
                          <button className="secondary-button" onClick={() => togglePartnerStatus(p.id, 'suspended')}>Suspend</button>
                        ) : (
                          <button className="secondary-button" onClick={() => togglePartnerStatus(p.id, 'active')}>Activate</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'audits' && (
        <div className="dashboard-section">
          <h3>Audit logs</h3>
          <p className="status-text">Latest 50 admin actions.</p>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Admin</th>
                <th>Landlord</th>
                <th>Action</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 && (
                <tr>
                  <td colSpan="5" className="status-text">No audit entries yet.</td>
                </tr>
              )}
              {auditLogs.map((log) => (
                <tr key={log.id}>
                  <td data-label="Time">{log.created_at ? new Date(log.created_at).toLocaleString() : '—'}</td>
                  <td data-label="Admin">{log.admin_id || '—'}</td>
                  <td data-label="Landlord">{log.landlord_id || '—'}</td>
                  <td data-label="Action">{log.action}</td>
                  <td data-label="Details">
                    <details>
                      <summary>View</summary>
                      <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify({ metadata: log.metadata || {}, before: log.before_state || null, after: log.after_state || null }, null, 2)}</pre>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'support' && (
        <div className="messages-layout">
          <div className="dashboard-section messages-panel messages-threads-panel">
            <div className="section-header">
              <h3>Support threads</h3>
            </div>
            {supportThreadsLoading ? (
              <p className="status-text">Loading support threads...</p>
            ) : supportThreadsError ? (
              <p className="error-message">{supportThreadsError}</p>
            ) : supportThreads.length === 0 ? (
              <p className="status-text">No support threads yet.</p>
            ) : (
              <div className="inquiry-list">
                {supportThreads.map((thread) => {
                  const isSelected = supportSelectedThread && thread.id === supportSelectedThread.id;
                  const subtitle = thread.user_email || `User ${thread.user_id}`;
                  return (
                    <div
                      key={`support-thread-${thread.id}`}
                      className="inquiry-card"
                      onClick={() => setSupportSelectedThread(thread)}
                      style={{ cursor: 'pointer', border: isSelected ? '1px solid #2f6fed' : undefined }}
                    >
                      <p><strong>{subtitle}</strong></p>
                      <p className="status-text">{thread.user_type}</p>
                      <p className="status-text" style={{ marginTop: '0.35rem' }}>{thread.latest_message || 'No messages yet.'}</p>
                      {thread.latest_message_at && <p className="timestamp">{new Date(thread.latest_message_at).toLocaleString()}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="dashboard-section messages-panel messages-chat-panel">
            <div className="section-header">
              <h3>Support chat</h3>
              {supportSelectedThread && (
                <span className="report-chip report-chip--muted">
                  {supportSelectedThread.user_email || `User ${supportSelectedThread.user_id}`} · {supportSelectedThread.user_type}
                </span>
              )}
            </div>
            {!supportSelectedThread ? (
              <p className="status-text">Select a support thread to view messages.</p>
            ) : supportMessagesLoading ? (
              <p className="status-text">Loading messages...</p>
            ) : (
              <>
                {supportMessages.length === 0 ? (
                  <p className="status-text">No messages yet.</p>
                ) : (
                  <div className="lease-chat-thread">
                    {supportMessages.map((msg) => (
                      <div key={`support-msg-${msg.id}`} className={`lease-chat-message ${msg.sender_type === 'support' ? 'landlord' : 'tenant'}`}>
                        <p>{msg.message_text}</p>
                        <span>{msg.sender_type === 'support' ? 'Support' : (msg.sender_email || 'User')}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="lease-chat-compose" style={{ marginTop: '0.75rem' }}>
                  {supportReplyError && <p className="error-message">{supportReplyError}</p>}
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent-color)', marginBottom: '0.35rem', padding: '0.3rem 0.5rem', background: 'rgba(40,182,200,0.06)', borderRadius: '6px', border: '1px solid rgba(40,182,200,0.15)' }}>
                    Replying to: {supportSelectedThread.user_email || `User ${supportSelectedThread.user_id}`}
                  </div>
                  <textarea
                    className="comment-box"
                    rows="2"
                    value={supportReplyText}
                    onChange={(e) => setSupportReplyText(e.target.value)}
                    placeholder={`Reply to ${supportSelectedThread.user_email || 'user'}...`}
                    disabled={supportMessagesLoading}
                  />
                  <button className="secondary-button" onClick={handleSupportReply} disabled={supportReplySending || supportMessagesLoading || !supportReplyText.trim()}>
                    {supportReplySending ? 'Sending...' : `Send to ${supportSelectedThread.user_email || 'user'}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="dashboard-section">
          <div className="section-header">
            <h3>Activity log</h3>
          </div>
          <div className="form-grid" style={{ marginBottom: '1rem' }}>
            <label className="form-label">
              User type
              <select
                className="input-field"
                value={activityFilters.userType}
                onChange={(e) => setActivityFilters((prev) => ({ ...prev, userType: e.target.value }))}
              >
                <option value="">All</option>
                <option value="landlord">Landlord</option>
                <option value="tenant">Tenant</option>
              </select>
            </label>
            <label className="form-label">
              User ID
              <input
                className="input-field"
                value={activityFilters.userId}
                onChange={(e) => setActivityFilters((prev) => ({ ...prev, userId: e.target.value }))}
                placeholder="e.g. 12"
              />
            </label>
            <label className="form-label">
              Event type
              <input
                className="input-field"
                value={activityFilters.eventType}
                onChange={(e) => setActivityFilters((prev) => ({ ...prev, eventType: e.target.value }))}
                placeholder="auth.login"
              />
            </label>
            <label className="form-label">
              Search
              <input
                className="input-field"
                value={activityFilters.search}
                onChange={(e) => setActivityFilters((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="entity or metadata"
              />
            </label>
          </div>

          {activityLoading ? (
            <p className="status-text">Loading activity logs...</p>
          ) : activityError ? (
            <p className="error-message">{activityError}</p>
          ) : activityLogs.length === 0 ? (
            <p className="status-text">No activity yet.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Event</th>
                  <th>Entity</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {activityLogs.map((row) => (
                  <tr key={`activity-${row.id}`}>
                    <td data-label="Time">{row.occurred_at ? new Date(row.occurred_at).toLocaleString() : '—'}</td>
                    <td data-label="User">{row.user_type || '—'} {row.user_id ? `#${row.user_id}` : ''}</td>
                    <td data-label="Event">{row.event_type}</td>
                    <td data-label="Entity">{row.entity_type || '—'} {row.entity_id || ''}</td>
                    <td data-label="Details">
                      <details>
                        <summary>View</summary>
                        <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify({ ip: row.ip_address, source: row.source, metadata: row.metadata || {} }, null, 2)}</pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="form-actions" style={{ marginTop: '1rem' }}>
            <button
              className="secondary-button"
              onClick={() => setActivityPagination((prev) => ({ ...prev, offset: Math.max(prev.offset - prev.limit, 0) }))}
              disabled={activityPagination.offset === 0}
            >
              Previous
            </button>
            <button
              className="secondary-button"
              onClick={() => setActivityPagination((prev) => ({ ...prev, offset: prev.offset + prev.limit }))}
              disabled={activityPagination.offset + activityPagination.limit >= activityPagination.total}
            >
              Next
            </button>
            <span className="status-text" style={{ marginLeft: '0.75rem' }}>
              {activityPagination.total} total
            </span>
          </div>
        </div>
      )}

      {activeTab === 'traffic' && (
        <div className="dashboard-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>Live Traffic</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <select className="form-control" style={{ maxWidth: '140px', fontSize: '0.85rem' }} value={trafficDays} onChange={(e) => setTrafficDays(Number(e.target.value))}>
                <option value={1}>Today</option>
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
              <span className="status-text" style={{ fontSize: '0.75rem' }}>Auto-refreshes every 15s</span>
            </div>
          </div>

          {trafficLoading && !trafficData && <p className="status-text" style={{ marginTop: '1rem' }}>Loading traffic data…</p>}

          {trafficData && (
            <>
            {/* Active filters bar */}
            {(trafficFilters.page || trafficFilters.referrer || trafficFilters.device || trafficFilters.user_type) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.75rem', alignItems: 'center' }}>
                <span className="status-text" style={{ fontSize: '0.8rem', marginRight: '0.25rem' }}>Filtered by:</span>
                {trafficFilters.page && (
                  <button className="ghost-button" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', border: '1px solid var(--primary-color, #2f6fed)', borderRadius: '12px', background: 'var(--surface-color, #f0f6ff)', cursor: 'pointer' }}
                    onClick={() => setTrafficFilters(f => ({ ...f, page: null }))}>
                    Page: {trafficFilters.page} ✕
                  </button>
                )}
                {trafficFilters.referrer && (
                  <button className="ghost-button" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', border: '1px solid var(--primary-color, #2f6fed)', borderRadius: '12px', background: 'var(--surface-color, #f0f6ff)', cursor: 'pointer' }}
                    onClick={() => setTrafficFilters(f => ({ ...f, referrer: null }))}>
                    Source: {trafficFilters.referrer} ✕
                  </button>
                )}
                {trafficFilters.device && (
                  <button className="ghost-button" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', border: '1px solid var(--primary-color, #2f6fed)', borderRadius: '12px', background: 'var(--surface-color, #f0f6ff)', cursor: 'pointer' }}
                    onClick={() => setTrafficFilters(f => ({ ...f, device: null }))}>
                    Device: {trafficFilters.device} ✕
                  </button>
                )}
                {trafficFilters.user_type && (
                  <button className="ghost-button" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', border: '1px solid var(--primary-color, #2f6fed)', borderRadius: '12px', background: 'var(--surface-color, #f0f6ff)', cursor: 'pointer' }}
                    onClick={() => setTrafficFilters(f => ({ ...f, user_type: null }))}>
                    Type: {trafficFilters.user_type} ✕
                  </button>
                )}
                <button className="ghost-button" style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', color: 'var(--danger-color, #ef4444)', cursor: 'pointer' }}
                  onClick={() => setTrafficFilters({ page: null, referrer: null, device: null, user_type: null })}>
                  Clear all
                </button>
              </div>
            )}
            {/* ── KPI Cards ── */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              <div className="dashboard-section" style={{ flex: '1 1 120px', padding: '1rem', textAlign: 'center', borderLeft: '4px solid #059669' }}>
                <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Live Now</p>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 700, fontSize: '1.8rem', color: '#059669' }}>
                  {trafficData.live_visitors}
                  <span style={{ fontSize: '0.7rem', fontWeight: 400, marginLeft: '0.25rem' }}>●</span>
                </p>
              </div>
              <div className="dashboard-section" style={{ flex: '1 1 120px', padding: '1rem', textAlign: 'center' }}>
                <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Views Today</p>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 700, fontSize: '1.4rem' }}>{trafficData.today?.views_today || 0}</p>
              </div>
              <div className="dashboard-section" style={{ flex: '1 1 120px', padding: '1rem', textAlign: 'center' }}>
                <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Sessions Today</p>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 700, fontSize: '1.4rem' }}>{trafficData.today?.sessions_today || 0}</p>
              </div>
              <div className="dashboard-section" style={{ flex: '1 1 120px', padding: '1rem', textAlign: 'center' }}>
                <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>New Visitors</p>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 700, fontSize: '1.4rem' }}>{trafficData.today?.new_visitors || 0}</p>
              </div>
              <div className="dashboard-section" style={{ flex: '1 1 120px', padding: '1rem', textAlign: 'center' }}>
                <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Applications</p>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 700, fontSize: '1.4rem' }}>{trafficData.applications || 0}</p>
              </div>
              <div className="dashboard-section" style={{ flex: '1 1 120px', padding: '1rem', textAlign: 'center' }}>
                <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Signups</p>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 700, fontSize: '1.4rem' }}>{(trafficData.signups?.landlord_signups || 0) + (trafficData.signups?.tenant_signups || 0)}</p>
              </div>
            </div>

            {/* ── Daily Trend (CSS bar chart) ── */}
            {trafficData.trends?.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ margin: '0 0 0.75rem' }}>Daily Views</h4>
                {(() => {
                  const data = trafficData.trends;
                  const maxViews = Math.max(...data.map(d => d.views), 1);
                  const chartHeight = 180;
                  const totalViews = data.reduce((s, d) => s + d.views, 0);
                  const avgViews = data.length ? Math.round(totalViews / data.length) : 0;
                  const avgPct = (avgViews / maxViews) * chartHeight;

                  return (
                    <div style={{ position: 'relative' }}>
                      {/* Y-axis labels */}
                      <div style={{ position: 'absolute', left: 0, top: 0, height: chartHeight, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '0.65rem', color: 'var(--light-text-color, #9ca3af)', width: '30px', textAlign: 'right', paddingRight: '6px' }}>
                        <span>{maxViews}</span>
                        <span>{Math.round(maxViews / 2)}</span>
                        <span>0</span>
                      </div>
                      {/* Chart area */}
                      <div style={{ marginLeft: '34px', position: 'relative' }}>
                        {/* Average line */}
                        {avgViews > 0 && (
                          <div style={{ position: 'absolute', bottom: avgPct, left: 0, right: 0, borderBottom: '1px dashed var(--light-text-color, #9ca3af)', opacity: 0.4, zIndex: 1 }}>
                            <span style={{ position: 'absolute', right: 0, top: '-14px', fontSize: '0.6rem', color: 'var(--light-text-color, #9ca3af)' }}>avg {avgViews}</span>
                          </div>
                        )}
                        {/* Bars */}
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: chartHeight }}>
                          {data.map((d, i) => {
                            const barH = Math.max((d.views / maxViews) * chartHeight, d.views > 0 ? 4 : 1);
                            const isToday = String(d.date).slice(0, 10) === new Date().toISOString().slice(0, 10);
                            return (
                              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, justifyContent: 'flex-end', height: '100%' }} title={`${String(d.date).slice(0,10)}: ${d.views} views, ${d.sessions} sessions`}>
                                {d.views > 0 && <span style={{ fontSize: '0.6rem', color: 'var(--text-color, #374151)', fontWeight: 600, marginBottom: '2px' }}>{d.views}</span>}
                                <div style={{
                                  width: '100%',
                                  maxWidth: '28px',
                                  height: barH,
                                  background: isToday ? '#059669' : 'var(--primary-color, #2f6fed)',
                                  borderRadius: '4px 4px 0 0',
                                  transition: 'height 0.3s ease',
                                  opacity: d.views === 0 ? 0.15 : 1
                                }} />
                              </div>
                            );
                          })}
                        </div>
                        {/* X-axis labels */}
                        <div style={{ display: 'flex', gap: '3px', marginTop: '4px' }}>
                          {data.map((d, i) => {
                            // Show label for first, last, and every ~3rd day to avoid crowding
                            const showLabel = i === 0 || i === data.length - 1 || (data.length <= 14) || (i % Math.ceil(data.length / 10) === 0);
                            return (
                              <div key={i} style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                                {showLabel && (
                                  <span style={{ fontSize: '0.6rem', color: 'var(--light-text-color, #9ca3af)', whiteSpace: 'nowrap' }}>
                                    {(() => { try { const dt = new Date(String(d.date).slice(0, 10) + 'T12:00:00'); return isNaN(dt) ? String(d.date).slice(5) : dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }); } catch { return String(d.date).slice(5); } })()}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ── Hourly Activity (last 24h) ── */}
            {trafficData.hourly?.length > 0 && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ margin: '0 0 0.5rem' }}>Hourly Activity (last 24h)</h4>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1px', height: '60px' }}>
                  {Array.from({ length: 24 }, (_, h) => {
                    const entry = trafficData.hourly.find(e => e.hour === h);
                    const views = entry?.views || 0;
                    const maxH = Math.max(...trafficData.hourly.map(e => e.views), 1);
                    const pct = (views / maxH) * 100;
                    const now = new Date().getHours();
                    return (
                      <div key={h} style={{ flex: 1, minWidth: 0 }} title={`${h}:00 — ${views} views`}>
                        <div style={{
                          width: '100%',
                          height: `${Math.max(pct, 2)}%`,
                          background: h === now ? '#059669' : 'var(--primary-color, #2f6fed)',
                          borderRadius: '2px 2px 0 0',
                          opacity: views === 0 ? 0.2 : 1
                        }} />
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                  <span style={{ fontSize: '0.55rem', color: 'var(--light-text-color, #9ca3af)' }}>12 AM</span>
                  <span style={{ fontSize: '0.55rem', color: 'var(--light-text-color, #9ca3af)' }}>12 PM</span>
                  <span style={{ fontSize: '0.55rem', color: 'var(--light-text-color, #9ca3af)' }}>11 PM</span>
                </div>
              </div>
            )}

            {/* ── Two-column: Top Pages + Referrers ── */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
              {/* Top Pages */}
              <div style={{ flex: '1 1 300px' }}>
                <h4 style={{ margin: '0 0 0.5rem' }}>Top Pages</h4>
                <div style={{ fontSize: '0.85rem' }}>
                  {(trafficData.top_pages || []).map((p, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid var(--border-color, #e5e7eb)', cursor: 'pointer', background: trafficFilters.page === (p.path || '/') ? 'var(--surface-color, #f0f6ff)' : 'transparent', borderRadius: '4px', paddingLeft: '0.25rem', paddingRight: '0.25rem' }}
                      onClick={() => setTrafficFilters(f => ({ ...f, page: f.page === (p.path || '/') ? null : (p.path || '/') }))}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }} title={p.path}>{p.path || '/'}</span>
                      <span className="status-text">{p.views} <span style={{ fontSize: '0.7rem' }}>({p.unique_sessions} uniq)</span></span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Referrers */}
              <div style={{ flex: '1 1 250px' }}>
                <h4 style={{ margin: '0 0 0.5rem' }}>Top Referrers</h4>
                <div style={{ fontSize: '0.85rem' }}>
                  {(trafficData.top_referrers || []).map((r, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid var(--border-color, #e5e7eb)', cursor: 'pointer', background: trafficFilters.referrer === r.source ? 'var(--surface-color, #f0f6ff)' : 'transparent', borderRadius: '4px', paddingLeft: '0.25rem', paddingRight: '0.25rem' }}
                      onClick={() => setTrafficFilters(f => ({ ...f, referrer: f.referrer === r.source ? null : r.source }))}>
                      <span>{r.source}</span>
                      <span className="status-text">{r.visits}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Two-column: Devices + User Types ── */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
              {/* Devices */}
              <div style={{ flex: '1 1 200px' }}>
                <h4 style={{ margin: '0 0 0.5rem' }}>Devices</h4>
                {(trafficData.devices || []).map((d, i) => {
                  const total = trafficData.devices.reduce((s, x) => s + x.sessions, 0) || 1;
                  const pct = Math.round((d.sessions / total) * 100);
                  const isActive = trafficFilters.device === d.device;
                  return (
                    <div key={i} style={{ marginBottom: '0.5rem', cursor: 'pointer', background: isActive ? 'var(--surface-color, #f0f6ff)' : 'transparent', borderRadius: '4px', padding: '0.15rem 0.25rem' }}
                      onClick={() => setTrafficFilters(f => ({ ...f, device: f.device === d.device ? null : d.device }))}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                        <span>{d.device}</span>
                        <span className="status-text">{pct}% ({d.sessions})</span>
                      </div>
                      <div style={{ height: '6px', background: 'var(--border-color, #e5e7eb)', borderRadius: '3px', marginTop: '2px' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: isActive ? '#059669' : 'var(--primary-color, #2f6fed)', borderRadius: '3px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* User Types */}
              <div style={{ flex: '1 1 200px' }}>
                <h4 style={{ margin: '0 0 0.5rem' }}>Visitor Types</h4>
                {(trafficData.user_types || []).map((u, i) => {
                  const total = trafficData.user_types.reduce((s, x) => s + x.sessions, 0) || 1;
                  const pct = Math.round((u.sessions / total) * 100);
                  const colors = { visitor: '#6b7280', landlord: '#2f6fed', tenant: '#059669', admin: '#f59e0b' };
                  const isActive = trafficFilters.user_type === (u.user_type || 'unknown');
                  return (
                    <div key={i} style={{ marginBottom: '0.5rem', cursor: 'pointer', background: isActive ? 'var(--surface-color, #f0f6ff)' : 'transparent', borderRadius: '4px', padding: '0.15rem 0.25rem' }}
                      onClick={() => setTrafficFilters(f => ({ ...f, user_type: f.user_type === (u.user_type || 'unknown') ? null : (u.user_type || 'unknown') }))}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                        <span style={{ textTransform: 'capitalize' }}>{u.user_type || 'unknown'}</span>
                        <span className="status-text">{pct}% ({u.sessions})</span>
                      </div>
                      <div style={{ height: '6px', background: 'var(--border-color, #e5e7eb)', borderRadius: '3px', marginTop: '2px' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: isActive ? '#f59e0b' : (colors[u.user_type] || '#6b7280'), borderRadius: '3px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Live Event Feed ── */}
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem' }}>Live Feed</h4>
              <div style={{ maxHeight: '300px', overflow: 'auto', fontSize: '0.8rem', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px' }}>
                {trafficFeed.length === 0 ? (
                  <p className="status-text" style={{ padding: '1rem', textAlign: 'center' }}>No recent events</p>
                ) : (
                  trafficFeed.map((ev, i) => {
                    const time = new Date(ev.created_at).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                    const icons = { page_view: '👁', property_view: '🏠', application_submitted: '📝', property_created: '🏗', document_uploaded: '📎' };
                    const icon = icons[ev.event_name] || '·';
                    const typeColor = { visitor: '#6b7280', landlord: '#2f6fed', tenant: '#059669' };
                    return (
                      <div key={ev.id || i} style={{
                        display: 'flex', gap: '0.5rem', padding: '0.35rem 0.75rem', alignItems: 'center',
                        borderBottom: i < trafficFeed.length - 1 ? '1px solid var(--border-color, #f3f4f6)' : 'none',
                        background: i === 0 ? 'var(--surface-color, #f9fafb)' : 'transparent'
                      }}>
                        <span style={{ flex: '0 0 60px', color: 'var(--light-text-color, #9ca3af)', fontSize: '0.75rem' }}>{time}</span>
                        <span style={{ flex: '0 0 20px' }}>{icon}</span>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.path || ev.page_url || '-'}</span>
                        <span style={{ flex: '0 0 60px', fontSize: '0.7rem', color: typeColor[ev.user_type] || '#6b7280', textTransform: 'capitalize' }}>{ev.user_type || 'visitor'}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            </>
          )}
        </div>
      )}
    </main>
  );
}

export default AdminDashboard;
